import React from 'react';
import { Heart, Shield, Award, Users, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { therapeuticDiets } from '../mock/data';

const TherapeuticDiets = () => {
  const benefits = [
    {
      icon: <Heart className="h-8 w-8 text-red-500" />,
      title: "Patient-Centered Care",
      description: "Designed specifically for individuals with swallowing difficulties and dietary restrictions"
    },
    {
      icon: <Shield className="h-8 w-8 text-green-500" />,
      title: "MOH Compliance",
      description: "Full compliance with Ministry of Health standards and regulations"
    },
    {
      icon: <Award className="h-8 w-8 text-yellow-500" />,
      title: "IDDSI Certified",
      description: "Certified under International Dysphagia Diet Standardisation Initiative"
    },
    {
      icon: <Users className="h-8 w-8 text-blue-500" />,
      title: "Expert Team",
      description: "Certified food hygiene officers and specialized diet experts"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-red-600 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Therapeutic Diets</h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Specialized nutrition solutions designed for individuals with swallowing difficulties 
              and specific dietary requirements, fully compliant with IDDSI standards.
            </p>
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Comprehensive Therapeutic Diet Solutions
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  At First Cuisine, we have the expertise and facilities to provide therapeutic diets 
                  tailored to meet the nutritional needs of individuals with specific health conditions. 
                  Our team of trained culinary and dietary professionals ensures that meals are 
                  nutritionally balanced, safe, and appealing, while adhering to medical and 
                  institutional dietary guidelines.
                </p>
                <p>
                  We cater to a variety of therapeutic requirements, including low-fibre, low-phosphate, 
                  and low-potassium diets, among others, supporting healthcare institutions, senior care 
                  facilities, and patients with special dietary needs.
                </p>
              </div>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1631021967261-c57ee4dfa9bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMHZlZ2V0YWJsZXMlMjBiYXNrZXRzfGVufDB8fHx8MTc2MjA1NTgwOHww&ixlib=rb-4.1.0&q=85"
                alt="Fresh vegetables and ingredients for therapeutic diets"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* IDDSI Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_foodholdings/artifacts/s8m98xma_iddsi%20-%20image.png"
                alt="IDDSI Framework - International Dysphagia Diet Standardisation Initiative"
                className="w-full h-auto object-contain rounded-lg shadow-lg"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                IDDSI-Compliant Catering Services
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  We specialize in providing nutritionally balanced meals tailored to meet IDDSI 
                  guidelines, ensuring safe and enjoyable dining for individuals with swallowing 
                  difficulties. Our team carefully prepares meals in various textures, including 
                  Easy to Chew, Soft & Bite-Sized, Minced & Moist, and Pureed—to suit each 
                  client's specific needs.
                </p>
                <p>
                  All dishes are crafted with attention to flavor, appearance, and nutritional 
                  value, making mealtime both safe and satisfying.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Choose Our Therapeutic Diets
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Combining medical expertise with culinary excellence to deliver safe, 
              nutritious, and appetizing meals for specialized dietary needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications & Standards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Certifications & Standards
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our commitment to quality is backed by rigorous certifications and 
              adherence to international standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardContent className="p-6">
                <Award className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Basic Food Hygiene</h3>
                <p className="text-sm text-gray-600">Foundation certification for food safety practices</p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-6">
                <Shield className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Advanced Food Hygiene Officer</h3>
                <p className="text-sm text-gray-600">Advanced certification for food safety management</p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardContent className="p-6">
                <Heart className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900 mb-2">Eatsafe SG (IDDSI)</h3>
                <p className="text-sm text-gray-600">International dysphagia diet standardization compliance</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Need Specialized Therapeutic Diets?
          </h2>
          <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
            Contact our therapeutic diet specialists to discuss your specific requirements 
            and learn how we can support your healthcare facility or organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors">
              Contact Specialists
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3 rounded-lg font-semibold transition-colors">
              View All Services
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TherapeuticDiets;